import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  CommonFormElementSize,
  FloatingStrategy,
  PopupPlacement,
} from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { i18next, t } from '@/localization/localization'

import DapDSFeedback from '../feedback/feedback.component'
import DapDSFormLabel from '../form/form-label/form-label.component'
import DapDSIcon from '../icon/icon.component'
import DapDSIconButton from '../icon-button/icon-button.component'
import ArrowsArrowDownSLine from '../icons/arrows/arrows-arrow-down-s-line/arrows-arrow-down-s-line.component'
import SystemLoadingSpinner from '../icons/system/system-loading-spinner/system-loading-spinner.component'
import DapDSOptionGroup from '../option-group/option-group.component'
import DapDSOptionItem from '../option-item/option-item.component'
import DapDSOptionList from '../option-list/option-list.component'
import DapDSPopup from '../popup/popup.component'
import DapDSTooltip from '../tooltip/tooltip.component'
import DapDSTray from '../tray/tray.component'
import DapDSTypography from '../typography/typography.component'
import styles from './select.scss?inline'

/**
 * `dap-ds-select`
 * @summary A select is a form element that allows the user to select one option from a set.
 * @element dap-ds-select
 * @title - Select
 *
 * @event {{ value: string }} dds-change - Fired when the select value changes.
 * @event {{ void }} dds-blur - Emitted when the select loses focus.
 * @event {{ void }} dds-focus - Emitted when the select gains focus.
 * @event {{ void }} dds-opened - Emitted when the select dropdown is opened.
 * @event {{ void }} dds-closed - Emitted when the select dropdown is closed.
 *
 * @property {string} value - The value of the select.
 * @property {string} label - The label of the select.
 * @property {boolean} hideLabel - Visually hides the label while keeping it available to assistive technology. (default: false)
 * @property {string} description - The description of the select.
 * @property {string} tooltip - The tooltip of the select.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the select.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the select. Default is `sm`.
 * @property {boolean} disabled - Whether the select is disabled.
 * @property {boolean} required - Whether the select is required.
 * @property {boolean} readonly - Whether the select is readonly.
 * @property {boolean} autofocus - Whether the select is autofocus.
 * @property {string} feedback - The feedback of the select.
 * @property {negative | positive | warning} feedbackType - The feedback type of the select. Can be `negative`, `positive`, or `warning`.
 * @property {string} status - The status of the select. Can be `success` or `error`.
 * @property {boolean} optional - The optional state of the select.
 * @property {string} optionalLabel - The optional label of the select.
 * @property {string} requiredLabel - The required indicator of the select. (default: '*')
 * @property {boolean} subtle - The weight of the label. Default is `false`
 *
 * @csspart base - The main select container.
 * @csspart trigger - The trigger button of the select.
 * @csspart label - The label of the select.
 * @csspart description - The description of the select.
 * @csspart feedback - The feedback of the select.
 * @csspart tooltip - The tooltip of the select.
 * @csspart popup-base - The base of the popup.
 * @csspart option-list - The option list of the select.
 * @csspart option-item - The option item of the select.
 * @csspart option-item-base - The base of the option item.
 * @csspart option-item-label - The label of the option item.
 * @csspart option-item-prefix - The prefix of the option item.
 * @csspart option-item-suffix - The suffix of the option item.
 * @csspart indicator-icon - The indicator icon of the select.
 * @csspart mobile-tray - The mobile tray of the select.
 * @csspart mobile-content - The mobile content of the select.
 * @csspart mobile-body - The mobile body of the select.
 * @csspart mobile-header - The mobile header of the select.
 * @csspart mobile-footer - The mobile footer of the select.
 *
 * @cssproperty --dds-select-min-height - Minimum height of the select component (default: var(--dds-spacing-1000))
 * @cssproperty --dds-select-padding - Padding of the select component (default: var(--dds-spacing-300))
 * @cssproperty --dds-select-column-gap - Column gap of the select component (default: var(--dds-spacing-100))
 * @cssproperty --dds-select-border-color - Border color of the select component (default: var(--dds-border-neutral-base))
 * @cssproperty --dds-select-border-radius - Border radius of the select component (default: var(--dds-radius-base))
 * @cssproperty --dds-select-bg-color - Background color of the select component (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-select-text-color - Text color of the select component (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-select-icon-right - Right position of the icon (default: var(--dds-spacing-200))
 * @cssproperty --dds-select-icon-color - Color of the icon (default: var(--dds-icon-neutral-subtle))
 * @cssproperty --dds-select-text-margin-right - Right margin of the text (default: var(--dds-spacing-600))
 * @cssproperty --dds-select-disabled-border-color - Border color when disabled (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-select-disabled-bg-color - Background color when disabled (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-select-disabled-text-color - Text color when disabled (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-select-error-border-color - Border color in error state (default: var(--dds-border-negative-base))
 * @cssproperty --dds-select-success-border-color - Border color in success state (default: var(--dds-border-positive-base))
 * @cssproperty --dds-select-xs-min-height - Minimum height for extra small size (default: var(--dds-spacing-800))
 * @cssproperty --dds-select-xs-padding - Padding for extra small size (default: var(--dds-spacing-200))
 * @cssproperty --dds-select-xs-font-size - Font size for extra small size (default: var(--dds-font-sm))
 * @cssproperty --dds-select-lg-min-height - Minimum height for large size (default: var(--dds-spacing-1200))
 * @cssproperty --dds-select-lg-padding - Padding for large size (default: var(--dds-spacing-400))
 * @cssproperty --dds-select-lg-font-size - Font size for large size (default: var(--dds-font-lg))
 * @cssproperty --dds-select-lg-icon-right - Right position of the icon for large size (default: var(--dds-spacing-300))
 * @cssproperty --dds-select-lg-icon-color - Color of the icon for large size (default: var(--dds-text-icon-neutral-subtle))
 * @cssproperty --dds-select-popup-bg-color - Background color of the popup (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-select-icon-opened-transform - Transform of the icon when the select is opened (default: rotate(90deg))
 *
 * @slot - The option list of the select.
 * @slot indicator-icon - The indicator icon of the select.
 */
export default class DapDSSelect extends GenericFormElement {
  static tagName = 'dap-ds-select'

  static dependencies = {
    'dap-ds-icon-loading-spinner': SystemLoadingSpinner,
    'dap-ds-icon-arrow-down-s-line': ArrowsArrowDownSLine,
    'dap-ds-option-list': DapDSOptionList,
    'dap-ds-form-label': DapDSFormLabel,
    'dap-ds-tooltip': DapDSTooltip,
    'dap-ds-icon-button': DapDSIconButton,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-typography': DapDSTypography,
    'dap-ds-popup': DapDSPopup,
    'dap-ds-tray': DapDSTray,
    'dap-ds-feedback': DapDSFeedback,
  }

  /**
   * @ignore
   */
  @query('button') triggerButton!: HTMLButtonElement

  /**
   * @ignore
   */
  @query('dap-ds-option-list') optionList!: DapDSOptionList

  /** The popup placement of the select
   * @type {'top' | 'top-start' | 'top-end' | 'right' | 'right-start'| 'right-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** Whether the select dropdown is opened.*/
  @property({ type: Boolean, reflect: true }) opened? = false

  /** The placeholder of the select. */
  @property() placeholder: string | null

  /** The sync size of the select dropdown */
  @property({ type: Boolean }) sync?: boolean

  /** Whether the select is in mobile mode. */
  @property({ type: Boolean }) isMobile?: boolean = false

  /** Whether to render a native HTML select element for mobile-friendly option selection. */
  @property({ type: Boolean }) native?: boolean = false

  /** The loading state of the select. */
  @property({ type: Boolean }) loading?: boolean = false

  /** The max heigth of the select dropdown. */
  @property({ type: Number }) maxHeight?: number

  /** Whether the select dropdown indicator is animated or not */
  @property({ type: Boolean }) noAnimation?: boolean = false

  /** The floating strategy of the select dropdown
   * @type {'absolute' | 'fixed'}
   */
  @property({ reflect: true }) floatingStrategy: FloatingStrategy = 'absolute'

  /** Whether to keep popup open when trigger leaves viewport. */
  @property({ type: Boolean }) disableViewportAutoClose: boolean = false

  /**
   * @ignore
   */
  @state() selectedItem: any

  static readonly styles = unsafeCSS(styles)

  private languageChangeHandler = () => {
    // Force re-render when language changes to update selectedDisplayText
    this.requestUpdate()
  }

  connectedCallback() {
    super.connectedCallback()

    // Listen for language changes to update the displayed text
    i18next.on('languageChanged', this.languageChangeHandler)

    // Ensure native browser validation UI is suppressed by default,
    // similar to other form components (inputs, combobox, etc.)
    this.addEventListener('invalid', this.handleInvalid)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    // Clean up language change listener
    i18next.off('languageChanged', this.languageChangeHandler)

    this.removeEventListener('invalid', this.handleInvalid)
  }

  /**
   * Lifecycle method to handle property changes
   */
  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties)

    // Handle when value is set to undefined or null externally
    if (changedProperties.has('value')) {
      const newValue = this.value
      if (newValue === undefined || newValue === null) {
        if (this.selectedItem !== null) {
          this.selectedItem = null
          this.setValidity()
        }
      }
    }
  }

  private readonly handleClick = () => {
    if (this.readonly || this.disabled) {
      return
    }

    this.opened ? this.hideDropDown() : this.showDropDown()
  }

  private readonly handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab' || event.key === 'Shift') {
      return
    }

    if (this.readonly) {
      return
    }

    event.preventDefault()

    if (event.key === 'Enter' || event.key === ' ') {
      return this.opened ? this.hideDropDown() : this.showDropDown()
    }

    if (event.key === 'Escape') {
      this.hideDropDown()
      return this.focus()
    }

    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      if (!this.opened) {
        return this.showDropDown()
      }

      return
    }

    this.emit('dds-keydown', event)

    // typing
    this.showDropDown(event.key)
  }

  private readonly handleListKeyDown = (event: CustomEvent) => {
    if (event.detail.key === 'Escape') {
      this.hideDropDown()
    }
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  private readonly handlePopupAutoClose = () => {
    this.hideDropDown({ skipFocus: true })
  }

  private readonly handleFocus = () => {
    this.emit('dds-focus')
  }

  private readonly handleBlur = () => {
    this.emit('dds-blur')
  }

  formResetCallback() {
    this.internals.setValidity({})
    this.internals.setFormValue(this.defaultValue)
    this.value = this.defaultValue
    this.selectedItem = this.defaultValue
  }

  async showDropDown(key?: string) {
    if (this.opened) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.optionList.captureFocus(key)

    // Force update to ensure aria-activedescendant is set correctly
    await this.updateComplete

    this.emit('dds-opened')
  }

  async hideDropDown(options?: { skipFocus?: boolean }) {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.optionList.removeFocus()

    if (!options?.skipFocus) {
      this.focus()
    }

    this.emit('dds-closed')
  }

  get focusElement() {
    return this.triggerButton
  }

  /**
   * Get the active descendant element for aria-activedescendant
   * Returns the currently focused option when dropdown is open, otherwise null
   */
  getActiveDescendant() {
    if (!this.opened) {
      return null
    }

    const activeOption = this.optionList?.getActiveOption()
    return activeOption
  }

  /**
   * Get the aria-describedby value, combining description and feedback IDs
   */
  private getAriaDescribedBy(): string | undefined {
    const ids: string[] = []
    if (this.description) {
      ids.push('description')
    }
    if (this.feedback) {
      ids.push('feedback')
    }
    return ids.length > 0 ? ids.join(' ') : undefined
  }

  // custom validation
  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing:
          this.required &&
          (!this.selectedItem ||
            this.selectedItem.value === '' ||
            this.value === '' ||
            this.value === undefined ||
            this.value === null),
      })
    }
  }

  get validationMessage(): string {
    return t('form.validationMessage.select')
  }

  /** Get the current display text for the selected item, dynamically fetching from the option */
  get selectedDisplayText(): string | null {
    if (!this.selectedItem) return null

    // Try to get the current text from the actual option element (reactive to language changes)
    if (this.optionList) {
      const currentOption = Array.from(
        this.optionList.querySelectorAll('dap-ds-option-item'),
      ).find((opt: any) => opt.value === this.selectedItem.value)

      if (currentOption) {
        return (currentOption as any).optionText
      }
    }

    // Fallback to cached text if option not found
    return this.selectedItem.text
  }

  checkValidity() {
    const hasValue =
      !!this.selectedItem &&
      this.selectedItem.value !== '' &&
      this.value !== '' &&
      this.value !== undefined &&
      this.value !== null

    return this.required ? hasValue : true
  }

  reportValidity() {
    return this.required ? this.checkValidity() : true
  }

  setValidity() {
    const isValid = this.checkValidity()

    if (!isValid) {
      this.internals.setValidity(
        {
          valueMissing:
            this.required &&
            (!this.selectedItem ||
              this.selectedItem.value === '' ||
              this.value === '' ||
              this.value === undefined ||
              this.value === null),
        },
        this.validationMessage,
        this.triggerButton,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
    this.focus()
  }

  private readonly handleSelectedChange = (event: CustomEvent) => {
    const val = Object.keys(event.detail).length ? event.detail : null
    const newValue = val?.value
    // The option-list echoes dds-option-change whenever its value is synced from
    // ours (searchOnValueSet), so a programmatic `value` assignment would surface
    // as a user change. A genuine selection changes the value here; a programmatic
    // one has already set it before the echo arrives. Emit only on a real change.
    const changed = newValue !== this.value
    this.selectedItem = val
    this.hideDropDown()
    this.value = newValue
    if (changed) {
      this.emit('dds-change', this.selectedItem)
    }

    this.setValidity()
  }

  private readonly handleOptionDeselect = () => {
    this.selectedItem = null
    this.value = undefined
    this.emit('dds-change', null)

    this.setValidity()
  }

  private readonly handleNativeChange = (event: Event) => {
    const select = event.target as HTMLSelectElement
    const value = select.value

    // Find matching option-item to get full item data
    const optionItem = this.querySelector(
      `dap-ds-option-item[value="${value}"]`,
    ) as DapDSOptionItem | null

    this.value = value
    this.selectedItem = optionItem
      ? { value, text: optionItem.optionText }
      : { value, text: value }

    this.emit('dds-change', this.selectedItem)
    this.setValidity()
  }

  private renderNativeOption(item: DapDSOptionItem) {
    return html`
      <option
        value=${item.value || ''}
        ?disabled=${item.disabled}
        ?selected=${item.value === this.value}>
        ${item.optionText}
      </option>
    `
  }

  private renderNativeOptgroup(group: DapDSOptionGroup) {
    const options = Array.from(
      group.querySelectorAll('dap-ds-option-item'),
    ) as DapDSOptionItem[]
    return html`
      <optgroup label=${group.label || ''} ?disabled=${group.disabled}>
        ${options.map(opt => this.renderNativeOption(opt))}
      </optgroup>
    `
  }

  private renderNativeOptions() {
    const children = Array.from(this.children)
    return children.map(child => {
      if (child.tagName === 'DAP-DS-OPTION-GROUP') {
        return this.renderNativeOptgroup(child as DapDSOptionGroup)
      } else if (child.tagName === 'DAP-DS-OPTION-ITEM') {
        return this.renderNativeOption(child as DapDSOptionItem)
      }
      return ''
    })
  }

  private renderNativeSelect() {
    return html`
      <div class="select-wrapper--native">
        <select
          class="${clsx('select', `select--${this.effectiveSize}`, {
            'select--disabled': this.disabled,
            'select--readonly': this.readonly,
            'select--success': this.status === 'success',
            'select--error': this.status === 'error',
            'select--loading': this.loading,
          })}"
          part="trigger"
          aria-labelledby=${ifDefined(this.label ? 'label' : undefined)}
          aria-describedby=${ifDefined(this.getAriaDescribedBy())}
          ?disabled=${this.disabled}
          ?required=${this.required}
          @change=${this.handleNativeChange}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}>
          ${this.placeholder
            ? html`<option value="" disabled ?selected=${!this.value}>
                ${this.placeholder}
              </option>`
            : ''}
          ${this.renderNativeOptions()}
        </select>
        <div class="icon" part="indicator-icon">
          ${this.loading
            ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
            : html`<slot name="indicator-icon"
                ><dap-ds-icon-arrow-down-s-line></dap-ds-icon-arrow-down-s-line
              ></slot>`}
        </div>
      </div>
    `
  }

  renderTriggerElement() {
    return html` <button
      slot="trigger"
      part="trigger"
      role="combobox"
      class="${clsx('select', `select--${this.effectiveSize}`, {
        'select--disabled': this.disabled,
        'select--readonly': this.readonly,
        'select--opened': this.opened,
        'select--loading': this.loading,
        'select--success': this.status === 'success',
        'select--error': this.status === 'error',
        'select--no-animation': this.noAnimation,
        'select--optional': this.optional,
      })}"
      aria-expanded=${this.readonly || this.disabled
        ? 'false'
        : this.opened
          ? 'true'
          : 'false'}
      aria-haspopup=${ifDefined(
        this.readonly || this.disabled ? undefined : 'listbox',
      )}
      aria-labelledby=${ifDefined(this.label ? 'label' : undefined)}
      aria-label=${ifDefined(
        this.label
          ? undefined
          : this.ariaLabel ||
              this.selectedDisplayText ||
              this.placeholder ||
              undefined,
      )}
      aria-describedby=${ifDefined(this.getAriaDescribedBy())}
      aria-disabled=${this.disabled}
      aria-controls="option-list"
      aria-activedescendant=${ifDefined(this.getActiveDescendant()?.id)}
      ?required=${this.required}
      ?readonly=${this.readonly}
      ?autofocus=${this.autofocus}
      ?disabled=${this.disabled}
      tabindex=${this.readonly || this.disabled ? -1 : 0}
      @blur=${this.handleBlur}
      @focus=${this.handleFocus}
      @keydown=${this.handleKeyDown}
      @click=${this.handleClick}>
      <span class="text">${this.selectedDisplayText || this.placeholder}</span>
      <div
        class="${clsx('icon', {
          'icon--opened': this.opened,
          'icon--no-animation': this.noAnimation,
        })}"
        part="indicator-icon">
        ${this.loading
          ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
          : html`<slot name="indicator-icon"
              ><dap-ds-icon-arrow-down-s-line></dap-ds-icon-arrow-down-s-line
            ></slot>`}
      </div>
    </button>`
  }

  renderOptionList() {
    return html` <dap-ds-option-list
      class="option-list"
      part="option-list"
      exportparts="option-item, option-item-base, option-item-label, option-item-prefix, option-item-suffix"
      id="option-list"
      aria-labelledby=${ifDefined(this.label ? 'label' : undefined)}
      searchMode="typehead"
      size=${ifDefined(this.effectiveSize)}
      value=${this.value}
      disableDelaySet
      @dds-option-change=${this.handleSelectedChange}
      @dds-keydown=${this.handleListKeyDown}
      @dds-option-deselect=${this.handleOptionDeselect}>
      <slot></slot>
    </dap-ds-option-list>`
  }

  render() {
    return html` <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id="label"
          .size=${this.effectiveSize}
          .required=${this.required}
          .requiredLabel=${this.requiredLabel}
          .optional=${this.optional}
          .optionalLabel=${this.optionalLabel}
          .subtle=${this.subtle}
          .disabled=${this.disabled}
          class=${clsx('label')}
          .noMargin=${!!this.description}
          for="input"
          .hideLabel=${this.hideLabel}
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            clean
            slot="trigger"
            size=${this.effectiveSize as 'xs' | 'sm' | 'md' | 'lg'}
            iconSize=${this.effectiveSize === 'xs' ? '16' : '22'}
            aria-label=${this.tooltipAriaLabel}
            class="input-group__toggle-button">
            <dap-ds-icon
              name="information-line"
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          class=${clsx('input-description')}
          size=${ifDefined(this.effectiveSize as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `}
      <div>
        ${this.native
          ? this.renderNativeSelect()
          : !this.isMobile
            ? html`<dap-ds-popup
                floatingStrategy=${ifDefined(this.floatingStrategy)}
                part="base"
                exportparts="popup:popup-base"
                class="select-popup"
                ?opened=${this.opened}
                ?hasArrow=${false}
                overflow
                placement=${this.placement}
                maxHeight=${ifDefined(this.maxHeight)}
                ?sync=${this.sync}
                ?disableViewportAutoClose=${this.disableViewportAutoClose}
                @dds-close=${this.handlePopupAutoClose}
                size=${ifDefined(this.effectiveSize) as 'lg' | 'sm' | 'xs'}>
                ${this.renderTriggerElement()} ${this.renderOptionList()}
              </dap-ds-popup>`
            : html`
                ${this.renderTriggerElement()}
                <dap-ds-tray
                  part="mobile-tray"
                  exportparts="content: mobile-content, body: mobile-body, header: mobile-header, footer: mobile-footer"
                  ?open=${this.opened}
                  closeButton="false"
                  header="false"
                  >${this.renderOptionList()}</dap-ds-tray
                >
              `}
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.effectiveSize as 'lg' | 'sm' | 'xs')}"
          id="feedback"
          .subtle=${this.subtle}>
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
